import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { MapPin, Phone, Package, RefreshCw, CalendarPlus, FlaskConical } from 'lucide-react';

export default function NextDayPage() {
  const [data, setData] = useState({ deliveries: [], samples: [], date: '', dayName: '' });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try { const res = await api.getNextDayDeliveries(); setData(res.data); } catch (err) { console.error(err); }
    setLoading(false);
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const pendingCount = data.deliveries.filter(d => d.deliveryStatus === 'pending').length;
  const skippedCount = data.deliveries.filter(d => d.isSkipped || d.isOOT).length;

  // Group by apartment
  const grouped = {};
  data.deliveries.filter(d => !d.isSkipped && !d.isOOT).forEach(d => {
    const apt = d.apt || 'Unassigned';
    if (!grouped[apt]) grouped[apt] = [];
    grouped[apt].push(d);
  });

  return (
    <div data-testid="next-day-panel" className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>Next Day Preparation</h2>
          <p className="text-sm mt-1" style={{ color: '#A8A29E' }}>{data.dayName} {data.date} &middot; {pendingCount} deliveries to prepare</p>
        </div>
        <button data-testid="refresh-next-day" onClick={() => { setLoading(true); fetchData(); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium" style={{ background: '#2A241F', color: '#C8A96E', border: '1px solid rgba(200,169,110,0.2)' }}>
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl p-4" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
          <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>{Object.keys(grouped).length}</div>
          <div className="text-xs mt-1" style={{ color: '#A8A29E' }}>Apartments</div>
        </div>
        <div className="rounded-xl p-4" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
          <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit', color: '#C8A96E' }}>{pendingCount}</div>
          <div className="text-xs mt-1" style={{ color: '#A8A29E' }}>Subscriptions</div>
        </div>
        <div className="rounded-xl p-4" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
          <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit', color: '#60A5FA' }}>{data.samples?.length || 0}</div>
          <div className="text-xs mt-1" style={{ color: '#A8A29E' }}>Samples</div>
        </div>
        <div className="rounded-xl p-4" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
          <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit', color: '#FBBF24' }}>{skippedCount}</div>
          <div className="text-xs mt-1" style={{ color: '#A8A29E' }}>Skipped / OOT</div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12" style={{ color: '#78716C' }}>Loading...</div>
      ) : (
        <div className="space-y-4">
          {/* Samples for next day */}
          {data.samples?.length > 0 && (
            <div className="rounded-xl overflow-hidden" style={{ background: '#1E1A17', border: '1px solid rgba(96,165,250,0.2)' }}>
              <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid #2A241F', background: 'rgba(96,165,250,0.05)' }}>
                <FlaskConical className="w-4 h-4" style={{ color: '#60A5FA' }} />
                <span className="font-semibold text-sm" style={{ color: '#60A5FA' }}>Sample Orders ({data.samples.length})</span>
              </div>
              <div className="divide-y" style={{ borderColor: '#2A241F' }}>
                {data.samples.map(s => (
                  <div key={s.id} className="px-5 py-3 flex items-center gap-3">
                    <div className="flex-1">
                      <span className="font-medium text-sm" style={{ color: '#F5F5F4' }}>{s.name}</span>
                      <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: '#78716C' }}>
                        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{s.apt}{s.flat ? ` / ${s.flat}` : ''}</span>
                        <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{s.mobile}</span>
                      </div>
                      {s.items?.length > 0 && <div className="flex gap-1 mt-1">{s.items.map(it => <span key={it} className="px-1.5 py-0.5 rounded text-xs" style={{ background: '#100E0C', color: '#A8A29E', border: '1px solid #2A241F' }}>{it}</span>)}</div>}
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: 'rgba(96,165,250,0.1)', color: '#60A5FA', border: '1px solid rgba(96,165,250,0.2)' }}>Sample</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Subscription deliveries grouped by apartment */}
          {Object.keys(grouped).length === 0 && (!data.samples || data.samples.length === 0) ? (
            <div className="text-center py-12 rounded-xl" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)', color: '#78716C' }}>
              <Package className="w-12 h-12 mx-auto mb-3" style={{ color: '#2A241F' }} />
              <p>No deliveries to prepare for next day</p>
            </div>
          ) : Object.entries(grouped).map(([apt, deliveries], idx) => (
            <div key={apt} className="rounded-xl overflow-hidden fade-in" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)', animationDelay: `${idx * 80}ms` }}>
              <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #2A241F', background: 'rgba(200,169,110,0.03)' }}>
                <div className="flex items-center gap-2">
                  <CalendarPlus className="w-4 h-4" style={{ color: '#C8A96E' }} />
                  <span className="font-semibold text-sm" style={{ color: '#F5F5F4' }}>{apt}</span>
                </div>
                <span className="text-sm font-medium" style={{ color: '#C8A96E' }}>{deliveries.length} deliveries</span>
              </div>
              <div className="divide-y" style={{ borderColor: '#2A241F' }}>
                {deliveries.map(d => (
                  <div key={d.id} className="px-5 py-3 flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm" style={{ color: '#F5F5F4' }}>{d.name}</span>
                        <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#100E0C', color: '#78716C', border: '1px solid #2A241F' }}>Flat {d.flat}{d.floor ? ` F${d.floor}` : ''}</span>
                        {d.quantity > 1 && <span className="px-1.5 py-0.5 rounded-full text-xs font-semibold" style={{ background: 'rgba(200,169,110,0.15)', color: '#C8A96E' }}>x{d.quantity}</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: '#78716C' }}>
                        <span>{d.slot}</span>
                        <span>{d.mobile}</span>
                        <span>{d.planName || ''}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
